import { useDebouncedValue } from '@mantine/hooks';
import { VMultiSelect } from '@vision/ui/components';
import type { SchemaFieldValueOption } from '@vision/ui/services/schema-fields/types';
import { useLazyGetSchemaFieldValuesQuery } from '@vision/ui/services';
import { useCallback, useEffect, useMemo, useState } from 'react';
import qs from 'qs';

const SCHEMA_VALUES_PER_PAGE = 20;

interface TableDispatchSchemaValueSelectProps {
  accountId: string;
  fieldName: string;
  onChange: (values: string[]) => void;
  schemaFieldId: string;
  value: string[];
}

export function TableDispatchSchemaValueSelect({
  accountId,
  fieldName,
  onChange,
  schemaFieldId,
  value,
}: TableDispatchSchemaValueSelectProps) {
  const [items, setItems] = useState<SchemaFieldValueOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [getSchemaFieldValues, { data: response, isFetching }] = useLazyGetSchemaFieldValuesQuery();

  const loadPage = useCallback(
    (page: number) => {
      if (!accountId || !schemaFieldId) {
        return;
      }

      getSchemaFieldValues({
        accountId,
        schemaFieldId,
        query: qs.stringify({
          page,
          per_page: SCHEMA_VALUES_PER_PAGE,
          ...(debouncedSearchQuery ? { search_string: debouncedSearchQuery } : {}),
        }),
      });
    },
    [accountId, debouncedSearchQuery, getSchemaFieldValues, schemaFieldId],
  );

  useEffect(() => {
    setItems([]);
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    setItems((currentItems) =>
      Array.from(new Map(currentItems.concat(response.data).map((item) => [item.name, item])).values()),
    );
  }, [response]);

  const selectItems = useMemo(() => {
    const loadedItems = items.map((item) => ({ value: item.name, label: item.name }));
    const selectedItems = value.map((item) => ({ value: item, label: item }));

    return Array.from(new Map(selectedItems.concat(loadedItems).map((item) => [item.value, item])).values());
  }, [items, value]);

  const handleLoadMore = () => {
    const pagination = response?.meta?.pagination;

    if (pagination?.next) {
      loadPage(pagination.next);
    }
  };

  return (
    <VMultiSelect
      id={`report-table-dispatch-value-${schemaFieldId}`}
      data-testid={`input-report-table-dispatch-value-${schemaFieldId}`}
      label={fieldName}
      data={selectItems}
      loading={isFetching}
      searchable={true}
      value={value}
      onChange={onChange}
      onScrollToBottom={handleLoadMore}
      onSearchChange={setSearchQuery}
      withSelectAll={false}
      showAllSelectedPill={false}
      clearable={true}
    />
  );
}
